// printDocuments.js
// Central repository for printable document models and themes

/**
 * Card themes for business card printing.
 * Each theme includes background color, text color, accent color, and border color.
 */
export const CARD_THEMES = [
  {
    name: 'Ocean Blue',
    background: '#0a66c2',
    text: '#ffffff',
    accent: '#ffffff',
    borderColor: '#0a66c2'
  },
  {
    name: 'Charcoal',
    background: '#333333',
    text: '#ffffff',
    accent: '#ffffff',
    borderColor: '#333333'
  },
  {
    name: 'Warm Terracotta',
    background: '#d97706',
    text: '#ffffff',
    accent: '#ffffff',
    borderColor: '#d97706'
  },
  {
    name: 'Clean Light',
    background: '#f9fafb',
    text: '#111827',
    accent: '#0a66c2',
    borderColor: '#e5e7eb'
  }
];

/**
 * Build a data model for the flyer.
 * Caps services at 5 and provides a flag indicating if there are more.
 *
 * @param {Object} provider - The provider object
 * @returns {Object} Flyer model with name, description, services, address, images, url
 */
export function buildFlyerModel(provider) {
  if (!provider) {
    return {
      name: '',
      description: '',
      displayedServices: [],
      hasMore: false,
      address: '',
      images: [],
      url: ''
    };
  }

  const services = provider.servicesProvided || [];
  const displayedServices = services.slice(0, 5);
  const hasMore = services.length > 5;

  return {
    name: provider.name || '',
    description: provider.businessSummary || '',
    displayedServices,
    hasMore,
    address: buildAddress(provider),
    images: provider.imageList || [],
    url: provider.websiteUrl || ''
  };
}

/**
 * Build a data model for the business card.
 * Includes provider info and the selected theme.
 *
 * @param {Object} provider - The provider object
 * @param {string} publicProfileUrl - The public profile URL
 * @param {number} selectedThemeIndex - Index into CARD_THEMES
 * @returns {Object} Card model with provider data and theme
 */
export function buildCardModel(provider, publicProfileUrl, selectedThemeIndex) {
  if (!provider) {
    return {
      name: '',
      phone: '',
      email: '',
      shortDescription: '',
      url: publicProfileUrl || '',
      theme: CARD_THEMES[0],
      address: ''
    };
  }

  const theme = CARD_THEMES[selectedThemeIndex] || CARD_THEMES[0];

  return {
    name: provider.name || '',
    phone: provider.phoneNumber || '',
    email: provider.email || '',
    shortDescription: provider.shortDescription || '',
    url: publicProfileUrl || '',
    theme,
    address: buildAddress(provider)
  };
}

/**
 * Build a formatted address string from provider data.
 *
 * @param {Object} provider - The provider object
 * @returns {string} Formatted address
 */
function buildAddress(provider) {
  if (!provider) return '';

  const parts = [];
  if (provider.address) parts.push(provider.address);
  if (provider.city) parts.push(provider.city);
  if (provider.state) parts.push(provider.state);
  if (provider.postalCode) parts.push(provider.postalCode);

  return parts.filter(Boolean).join(', ');
}

